// In-memory cache service for optimizing performance
class CacheService {
  private cache = new Map<string, { data: any; expiry: number }>();
  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes

  // Set item in cache with optional TTL
  set(key: string, data: any, ttl?: number): void {
    const expiry = Date.now() + (ttl || this.defaultTTL);
    this.cache.set(key, { data, expiry });
  }

  // Get item from cache
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }

    // Check if expired
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }

    return item.data as T;
  }

  // Get or set pattern - execute function if not cached
  async getOrSet<T>(
    key: string, 
    fetchFn: () => Promise<T>, 
    ttl?: number
  ): Promise<T> {
    const cached = this.get<T>(key);
    
    if (cached !== null) {
      return cached;
    }

    const data = await fetchFn();
    this.set(key, data, ttl);
    return data;
  }

  // Delete specific key
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  // Clear cache by pattern
  deletePattern(pattern: string): void {
    const regex = new RegExp(pattern.replace('*', '.*'));
    
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  // Clear all cache
  clear(): void {
    this.cache.clear();
  }

  // Get cache stats
  getStats() {
    let totalSize = 0;
    let expiredCount = 0;
    const now = Date.now();

    for (const [key, value] of this.cache.entries()) {
      totalSize += JSON.stringify(value.data).length;
      if (now > value.expiry) {
        expiredCount++;
      }
    }

    return {
      size: this.cache.size,
      totalSize,
      expiredCount
    };
  }

  // Clean expired entries
  cleanup(): void {
    const now = Date.now();
    
    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiry) {
        this.cache.delete(key);
      }
    }
  }

  // Cache keys for common data
  static readonly KEYS = {
    LISTINGS: 'listings',
    USER_PROFILE: (userId: string) => `user_profile_${userId}`,
    SCORECARD: (npi: string) => `scorecard_${npi}`,
    BUSINESS_METRICS: 'business_metrics',
    COUNTY_STATS: 'county_stats',
    SUBSCRIPTION_STATUS: (userId: string) => `subscription_${userId}`,
    FAQ_ITEMS: 'faq_items',
    EMAIL_TEMPLATES: 'email_templates'
  };
}

export const cacheService = new CacheService();

// Auto cleanup every 10 minutes
setInterval(() => {
  cacheService.cleanup();
}, 10 * 60 * 1000);

export default cacheService;